import React from 'react';
import { Button, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { Files } from 'ontraccr-common';

import PropTypes from 'prop-types';

import Analytics from '../../helpers/Analytics';
import { validFileTypes, validateFileHeader, videoFileTypes } from '../../files/fileHelpers';
import { VIDEO_FLAG } from '../../constants/Flags';

import OntraccrFileUpload from './OntraccrFileUpload';

const defaultValidator = (file, paidFlags = []) => {
  const fileType = file.type?.toLowerCase();
  if (validFileTypes(paidFlags.includes(VIDEO_FLAG)).has(fileType)) {
    return true;
  }
  const fileTypeMsg = fileType ? `'${fileType}' files` : 'this file type';
  message.error(`Ontraccr doesn't support ${fileTypeMsg}`);
  Analytics.track('Files/UploadUnsupportedFileType', { fileType, size: file.size });
  return false;
};
const custom = ({
  addFile,
  customValidator = defaultValidator,
  shouldAddMetadata = false,
  paidFlags,
}) => async (args) => {
  const { file } = args;
  const { size = 0 } = file ?? {};
  const fileType = file?.type?.toLowerCase();
  if (size > Files.GB && videoFileTypes.includes(fileType)) {
    // Max 1GB
    message.error('Cannot upload files over 1GB');
    return;
  }
  setImmediate(() => args.onSuccess());
  const fileIsGood = await validateFileHeader(file);
  if (!fileIsGood) {
    message.error(`Ontraccr detected an issue with the file: ${file.name}`);
    return;
  }
  if (customValidator(file, paidFlags)) {
    const newFile = new File([file], file.name, { type: file.type });
    if (shouldAddMetadata) newFile.timestamp = DateTime.local().toMillis();
    addFile(newFile);
    message.success('File uploaded');
    Analytics.track('Files/UploadFromComputer', { fileType: newFile.type, size: newFile.size });
  }
};

export default function FileUpload({
  onUploadEnd,
  addFile,
  customValidator,
  style = {},
}) {
  const company = useSelector((state) => state.settings.company);
  return (
    <div className="live-feed-file-upload" style={style}>
      <OntraccrFileUpload
        name="fileupload"
        multiple
        showUploadList={false}
        customRequest={custom({
          addFile,
          customValidator,
          shouldAddMetadata: company?.settings?.autoAddMetadataToPhotos,
          paidFlags: company?.paidFlags,
        })}
      />
      {onUploadEnd && (
      <Button
        className="live-feed-file-upload-button"
        icon={
          <CloseOutlined />
        }
        onClick={(e) => {
          e.stopPropagation();
          onUploadEnd();
        }}
      />
      )}
    </div>
  );
}

FileUpload.propTypes = {
  onUploadEnd: PropTypes.func,
  addFile: PropTypes.func.isRequired,
  customValidator: PropTypes.func,
  style: PropTypes.shape({}),
};

FileUpload.defaultProps = {
  onUploadEnd: null,
  customValidator: defaultValidator,
  style: {},
};
